import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { merchantPath } from "@/routes";
import { Input } from "@/components/ui/base/input";
import { Button } from "@/components/ui/base/button";
import { Badge } from "@/components/ui/base/badge";
import { DataTable } from "@/components/ui/data-table/DataTable";
import { CursorPagination } from "@/components/ui/data-table/CursorPagination";
import { DataTableFacetedFilter } from "@/components/ui/data-table/DataTableFacetedFilter";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/base/popover";
import { ArrowUpDown, Filter, Loader2, Search, X } from "lucide-react";
import { useStateMachine } from "@/core/arch/state_machine";
import {
  SEARCH_DEBOUNCE_MS,
  useDebouncedValue,
} from "@/hooks/useDebouncedValue";
import { MerchantsRepository } from "@/application/merchants/merchants_repository";
import { MerchantsListBloc } from "@/application/merchants/merchants_list_bloc";
import { searchIndexRepository } from "@/application/search/search_index_repository";
import type { Plan } from "@cpos/shared/entitlements";
import type { FilterChip } from "@/application/merchants/merchant_list_params";
import type {
  MerchantFacets,
  WindowFilterNotice,
} from "@/application/merchants/merchants_list_bloc";
import type {
  MerchantFilters,
  MerchantRow,
  OverallStage,
  OnboardingStage,
  IdentityStage,
} from "@/application/merchants/types";
import {
  LIST_PARAM_KEYS,
  activeChips,
  formatEt,
  paramsWithoutChip,
} from "@/application/merchants/merchant_list_params";
import { IdentityStageBadge } from "./components/IdentityStageBadge";
import { IdentityCollisionSheet } from "./components/IdentityCollisionSheet";

// ── Constants ────────────────────────────────────────────────────────

const STAGE_OPTIONS = [
  { label: "Onboarding", value: "onboarding" },
  { label: "Active", value: "active" },
  { label: "Suspended", value: "suspended" },
  { label: "Churned", value: "churned" },
];

const ONBOARDING_OPTIONS = [
  { label: "Registered", value: "registered" },
  { label: "Details Required", value: "details_required" },
  { label: "Verifying", value: "verifying" },
  { label: "Input Required", value: "input_required" },
  { label: "Setup Needed", value: "setup_needed" },
  { label: "Ready", value: "ready" },
  { label: "Active", value: "active" },
];

const IDENTITY_OPTIONS = [
  { label: "Verified", value: "verified" },
  { label: "Duplicate", value: "duplicate" },
  { label: "Legacy", value: "pending_backfill" },
  { label: "Retry pending", value: "failed" },
  { label: "Unrecoverable", value: "unrecoverable" },
  { label: "Unverified", value: "not_verified" },
];

const SUBSCRIPTION_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Trialing", value: "trialing" },
  { label: "Incomplete", value: "incomplete" },
  { label: "Past Due", value: "past_due" },
  { label: "Inactive", value: "inactive" },
];

const STAGE_VARIANT: Record<
  OverallStage,
  "success" | "warning" | "destructive" | "outline"
> = {
  active: "success",
  onboarding: "warning",
  suspended: "destructive",
  churned: "outline",
};

const PLAN_VARIANT: Record<Plan, "default" | "outline"> = {
  pro: "default",
  free: "outline",
};

const ONBOARDING_VARIANT: Record<
  OnboardingStage,
  "success" | "info" | "warning" | "destructive" | "outline"
> = {
  registered: "outline",
  details_required: "info",
  verifying: "info",
  input_required: "warning",
  setup_needed: "info",
  ready: "success",
  active: "success",
};

// ── Helpers ──────────────────────────────────────────────────────────

function formatDate(epochSeconds: number | null): string {
  if (!epochSeconds) return "—";
  return new Date(epochSeconds * 1000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function EtTime({ iso }: { iso: string | null }) {
  const ms = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(ms))
    return <span className="text-subtle-foreground">—</span>;
  return (
    <span className="text-muted-foreground text-sm whitespace-nowrap">
      {formatEt(ms)}
    </span>
  );
}

function formatLabel(value: string | null): string {
  if (!value) return "—";
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function arrFilterFn(
  row: { getValue: (id: string) => unknown },
  columnId: string,
  filterValue: string[],
): boolean {
  const value = row.getValue(columnId) as string | null;
  return filterValue.includes(value ?? "");
}

// ── Toolbar ─────────────────────────────────────────────────────────

interface MerchantsToolbarProps {
  filters: MerchantFilters;
  facets: MerchantFacets;
  onFilterChange: (patch: Partial<MerchantFilters>) => void;
  /** Clears every filter, including the URL-driven ones and their address parameters. */
  onReset: () => void;
  searchQuery: string;
  totalMatches: number;
  /** A keystroke is still settling, so the shown results are one query behind. */
  isSearching: boolean;
  onSearchQueryChange: (query: string) => void;
}

function MerchantsToolbar({
  filters,
  facets,
  onFilterChange,
  onReset,
  searchQuery,
  totalMatches,
  isSearching,
  onSearchQueryChange,
}: MerchantsToolbarProps) {
  const activeFilterCount =
    filters.overallStages.length +
    filters.onboardingStages.length +
    filters.identityStages.length +
    filters.subscriptionStatuses.length;
  const isFiltered = activeFilterCount > 0 || activeChips(filters).length > 0;

  return (
    <div className="flex items-center gap-2">
      {/* Search — matches the whole base as you type */}
      <div className="relative">
        <Search className="text-subtle-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
        <Input
          placeholder="Search name, owner, email, or ID…"
          className="w-80 pr-9 pl-9"
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
        />
        {isSearching && (
          <Loader2
            aria-label="Searching"
            className="text-subtle-foreground absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 animate-spin"
          />
        )}
      </div>
      {searchQuery.trim() !== "" && !isSearching && (
        <span className="text-subtle-foreground text-xs">
          {totalMatches} {totalMatches === 1 ? "match" : "matches"}
        </span>
      )}

      {/* Lifecycle stage filters */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5">
            <Filter className="h-3.5 w-3.5" />
            Filters
            {activeFilterCount > 0 && (
              <Badge
                variant="secondary"
                className="rounded-sm px-1 text-[10px] font-normal"
              >
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <div className="flex flex-col gap-3">
            <p className="text-subtle-foreground text-[11px]">
              Filters apply to every matching merchant
            </p>
            <div className="flex flex-col gap-2">
              <DataTableFacetedFilter
                title="Overall Stage"
                options={STAGE_OPTIONS}
                selected={filters.overallStages}
                facetCounts={facets.overallStage}
                onSelectedChange={(values) =>
                  onFilterChange({ overallStages: values as OverallStage[] })
                }
              />
              <DataTableFacetedFilter
                title="Onboarding"
                options={ONBOARDING_OPTIONS}
                selected={filters.onboardingStages}
                facetCounts={facets.onboardingStage}
                onSelectedChange={(values) =>
                  onFilterChange({
                    onboardingStages: values as OnboardingStage[],
                  })
                }
              />
              <DataTableFacetedFilter
                title="Identity"
                options={IDENTITY_OPTIONS}
                selected={filters.identityStages}
                facetCounts={facets.identityStage}
                onSelectedChange={(values) =>
                  onFilterChange({ identityStages: values as IdentityStage[] })
                }
              />
              <DataTableFacetedFilter
                title="Subscription"
                options={SUBSCRIPTION_OPTIONS}
                selected={filters.subscriptionStatuses}
                facetCounts={facets.subscriptionStatus}
                onSelectedChange={(values) =>
                  onFilterChange({ subscriptionStatuses: values })
                }
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {isFiltered && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => {
            onReset();
          }}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Reset filters</span>
        </Button>
      )}
    </div>
  );
}

// ── Filter chips ─────────────────────────────────────────────────────

function FilterChips({
  filters,
  windowFilterNotice,
  onRemove,
}: {
  filters: MerchantFilters;
  windowFilterNotice: WindowFilterNotice | null;
  onRemove: (chip: FilterChip) => void;
}) {
  const chips = activeChips(filters);
  if (chips.length === 0) return null;
  const timed = chips.some((c) => c.id !== "plan");
  return (
    <div className="flex flex-col gap-1 px-4 pt-2">
      <div className="flex flex-wrap items-center gap-2">
        {chips.map((chip) => (
          <Badge key={chip.id} variant="secondary" className="gap-1 pr-1">
            {chip.label}
            <button
              type="button"
              aria-label={`Remove filter ${chip.label}`}
              className="hover:bg-muted rounded-sm p-0.5"
              onClick={() => onRemove(chip)}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      {timed && windowFilterNotice === "index-predates-times" && (
        <p className="text-subtle-foreground text-xs">
          The search index has no signup or Pro times yet, so these filters
          match nothing. The next build, within 15 minutes, adds them.
        </p>
      )}
      {chips.length > 0 && windowFilterNotice === "no-index" && (
        <p className="text-subtle-foreground text-xs">
          Signup, Pro and plan filters need the search index, so they cannot
          apply to this live page.
        </p>
      )}
    </div>
  );
}

// ── Index freshness ──────────────────────────────────────────────────

/**
 * Makes index staleness visible rather than mysterious.
 *
 * The list is served from an artifact rebuilt every 15 minutes, so a merchant
 * created moments ago may not appear here yet — they remain findable by email
 * or ID, which resolve live.
 */
function IndexFreshness({
  generatedAt,
  indexAvailable,
  refreshing,
  onRefresh,
}: {
  generatedAt: string | null;
  indexAvailable: boolean;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  if (!indexAvailable) {
    return (
      <p className="text-subtle-foreground px-4 pt-2 text-xs">
        Search index unavailable — showing a single live page. Search and
        filters apply only to these merchants.
      </p>
    );
  }
  if (!generatedAt) return null;

  const time = new Date(generatedAt).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <p className="text-subtle-foreground flex items-center gap-1.5 px-4 pt-2 text-xs">
      <span>Data as of {time}</span>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        className="underline underline-offset-2 disabled:opacity-50"
      >
        {refreshing ? "Refreshing…" : "Refresh"}
      </button>
    </p>
  );
}

// ── Component ────────────────────────────────────────────────────────

export function MerchantsView() {
  const navigate = useNavigate();
  const [state, bloc] = useStateMachine(
    () =>
      new MerchantsListBloc(searchIndexRepository, new MerchantsRepository()),
  );

  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [collisionSheetBusinessId, setCollisionSheetBusinessId] = useState<
    string | null
  >(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // ── URL filters ──────────────────────────────────────────────────────
  //
  // The query string is the source of truth for the signup, Pro and plan
  // filters: links from the wrap-up posts open the list already narrowed, and
  // removing a chip edits the URL, which flows back into the bloc.

  useEffect(() => {
    void bloc.applyUrlParams(searchParams);
  }, [bloc, searchParams]);

  // ── Initial load ────────────────────────────────────────────────────

  useEffect(() => {
    void bloc.load();
  }, [bloc]);

  useEffect(() => {
    if (state.status === "loaded") setHasLoadedOnce(true);
  }, [state.status]);

  // ── Search ───────────────────────────────────────────────────────────
  //
  // The input stays live while the query that drives the table is debounced,
  // so a fast typist does not re-render the results on every keystroke. The
  // gap between the two is what the spinner reports.

  const debouncedQuery = useDebouncedValue(
    localSearchQuery,
    SEARCH_DEBOUNCE_MS,
  );

  useEffect(() => {
    // Skip when the query has not actually changed. On mount both are "", and
    // dispatching that would mark the bloc loaded with zero items while
    // load() is still awaiting the index — a flash of empty table.
    if (debouncedQuery === bloc.state.query) return;
    void bloc.setQuery(debouncedQuery);
  }, [debouncedQuery, bloc]);

  const isSearching = debouncedQuery !== localSearchQuery;

  // ── Columns ──────────────────────────────────────────────────────────

  const columns: ColumnDef<MerchantRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "plan",
        header: "Plan",
        cell: ({ row }) => {
          const plan = row.getValue("plan") as Plan | null;
          if (!plan) return <span className="text-subtle-foreground">—</span>;
          return (
            // eslint-disable-next-line security/detect-object-injection -- controlled enum key
            <Badge variant={PLAN_VARIANT[plan]}>{formatLabel(plan)}</Badge>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Signed up",
        cell: ({ row }) => <EtTime iso={row.getValue("createdAt")} />,
      },
      {
        accessorKey: "proSince",
        header: "Pro since",
        cell: ({ row }) => <EtTime iso={row.getValue("proSince")} />,
      },
      {
        accessorKey: "businessName",
        header: ({ column }) => (
          <button
            className="flex items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Business Name
            <ArrowUpDown className="h-3 w-3" />
          </button>
        ),
        cell: ({ row }) => (
          <span className="text-foreground font-medium">
            {row.getValue("businessName")}
          </span>
        ),
      },
      {
        accessorKey: "overallStage",
        header: "Overall Stage",
        cell: ({ row }) => {
          const stage = row.getValue("overallStage") as OverallStage | null;
          if (!stage) return <span className="text-subtle-foreground">—</span>;
          return (
            // eslint-disable-next-line security/detect-object-injection -- controlled enum key
            <Badge variant={STAGE_VARIANT[stage]}>{formatLabel(stage)}</Badge>
          );
        },
        filterFn: arrFilterFn,
      },
      {
        accessorKey: "onboardingStage",
        header: "Onboarding",
        cell: ({ row }) => {
          const stage = row.getValue(
            "onboardingStage",
          ) as OnboardingStage | null;
          if (!stage) return <span className="text-subtle-foreground">—</span>;
          return (
            // eslint-disable-next-line security/detect-object-injection -- controlled enum key
            <Badge variant={ONBOARDING_VARIANT[stage]}>
              {formatLabel(stage)}
            </Badge>
          );
        },
        filterFn: arrFilterFn,
      },
      {
        accessorKey: "identityStage",
        header: "Identity",
        cell: ({ row }) => {
          const stage = row.getValue("identityStage") as IdentityStage | null;
          if (!stage) return <span className="text-subtle-foreground">—</span>;
          const businessId = row.original.id;
          if (stage === "duplicate") {
            return (
              <span
                onClick={(e) => e.stopPropagation()}
                className="inline-block"
              >
                <IdentityStageBadge
                  stage={stage}
                  size="sm"
                  onClick={() => setCollisionSheetBusinessId(businessId)}
                />
              </span>
            );
          }
          return <IdentityStageBadge stage={stage} size="sm" />;
        },
        filterFn: arrFilterFn,
      },
      {
        accessorKey: "subscriptionStatus",
        header: "Subscription",
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {formatLabel(row.getValue("subscriptionStatus"))}
          </span>
        ),
        filterFn: arrFilterFn,
      },
      {
        accessorKey: "updatedAt",
        header: ({ column }) => (
          <button
            className="flex items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Last Updated
            <ArrowUpDown className="h-3 w-3" />
          </button>
        ),
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {formatDate(row.getValue("updatedAt"))}
          </span>
        ),
      },
    ],
    [],
  );

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-foreground text-2xl font-bold">Merchants</h1>
      </div>

      {state.status === "loading" && !hasLoadedOnce ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
        </div>
      ) : state.status === "error" ? (
        <div className="bg-card rounded-lg border px-4 py-8 text-center text-sm text-red-500">
          Failed to load merchants: {state.error}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={state.items}
          initialSorting={[{ id: "businessName", desc: false }]}
          onRowClick={(row) => navigate(merchantPath(row.id))}
          toolbar={() => (
            <>
              <FilterChips
                filters={state.filters}
                windowFilterNotice={state.windowFilterNotice}
                onRemove={(chip) =>
                  setSearchParams(paramsWithoutChip(searchParams, chip), {
                    replace: true,
                  })
                }
              />
              <MerchantsToolbar
                filters={state.filters}
                facets={state.facets}
                onFilterChange={(patch) => void bloc.setFilter(patch)}
                onReset={() => {
                  void bloc.resetFilters();
                  const next = new URLSearchParams(searchParams);
                  for (const key of LIST_PARAM_KEYS) next.delete(key);
                  setSearchParams(next, { replace: true });
                }}
                searchQuery={localSearchQuery}
                totalMatches={state.totalMatches}
                isSearching={isSearching}
                onSearchQueryChange={setLocalSearchQuery}
              />
            </>
          )}
          footer={
            <div className="flex flex-col gap-1">
              <IndexFreshness
                generatedAt={state.generatedAt}
                indexAvailable={state.indexAvailable}
                refreshing={state.status === "loading"}
                onRefresh={() => void bloc.refresh()}
              />
              <CursorPagination
                hasPrev={state.hasPrev}
                hasNext={state.hasNext}
                onPrev={() => void bloc.prevPage()}
                onNext={() => void bloc.nextPage()}
                loading={state.status === "loading"}
              />
            </div>
          }
        />
      )}

      {collisionSheetBusinessId && (
        <IdentityCollisionSheet
          businessId={collisionSheetBusinessId}
          onClose={() => setCollisionSheetBusinessId(null)}
        />
      )}
    </div>
  );
}
